import { ContractExcelColumnsService } from '../../service/data/contract-excel-columns.service';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserAccountService } from '../../../common/service/user-account.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { mailCommand } from '../../../message/model/const/command';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { CustomPropertyVisibilityType, LeaseAgreementStatus, SubleaseStatus, SubleaseStatusToString } from '../../model/const/enums';
import { CooperativeHousingContract } from '../../model/cooperative-housing-contract';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractCommandHandlerResolver } from '../../service/command/contract-command-handler-resolver.service';
import { CooperativeHousingContractCommandHandlerResolver } from '../../service/command/cooperative-housing-contract-command-handler-resolver.service';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { CooperativeHousingContractCommandFactory } from '../../service/command/factory/cooperative-housing-contract-command-factory.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { TransferCooperativeHousingCommandHandler } from '../../service/command/transfer-cooperative-housing-command-handler.service';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { CooperativeHousingContractService } from '../../service/data/cooperative-housing-contract.service';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString } from '../../../invoice/model/const/enum';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { LeaseAgreement } from '../../model/lease-agreement';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { standardCommand } from '../../../core/model/command';
import { CooperativeHousingLeaseAgreementListService } from '../../service/data/lease-agreement-list.service';
import { rentalObjectCommand } from '../../command/rental-object.command';

@Component({
    selector: 'app-cooperative-housing-contract-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
    providers: [
        ContractExcelColumnsService,
        ApproveContractCommandHandler,
        CooperativeHousingContractCommandHandlerResolver,
        ShowLeaseAgreementInvoicesCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        PrintContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        TransferCooperativeHousingCommandHandler,
        CooperativeHousingLeaseAgreementListService,
        { provide: ContractService, useExisting: CooperativeHousingContractService },
        { provide: CommandFactoryService, useClass: CooperativeHousingContractCommandFactory },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CooperativeHousingContractManagerComponent extends PagedObjectManagerComponent<CooperativeHousingContract> {
  constructor(
    activatedRoute: ActivatedRoute,
    columnDefinitionService: ColumnDefinitionService,
    excelColumns: ContractExcelColumnsService,
    dispatcher: CommandDispatcher,
    handlerResolver: CooperativeHousingContractCommandHandlerResolver,
    service: CooperativeHousingContractService,
    messageService: MessageService,
    navigationService: NavigationService,
    private _userAccount: UserAccountService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _companyService: CompanyService,
    private readonly _propertyService: PropertyService,
    private readonly _leaseAgreementListService: CooperativeHousingLeaseAgreementListService
  ) {
    super(service, messageService, navigationService, dispatcher, handlerResolver, _leaseAgreementListService);
    this.columnDefinition = columnDefinitionService.create('CooperativeHousingContract');
    this.preferencesKey = 'rent.cooperative-housing-contract.list';
    this.showExcelExportButton = true;
    this.excelFileName = 'Bostadsrättskontrakt';
    this.excelContext = 'CooperativeHousingContract';
    this.excelAdditionalSheetItems = [
      { binding: 'additionalSheet.leaseAgreementRows', caption: 'EXCEL_EXPORT_SETTINGS.LEASE_AGREEMENT_ROWS', selected: false },
    ];
    this.excelColumnDefinition = [...this.columnDefinition];
    this.AddSubscription(
      activatedRoute.queryParamMap.subscribe(map => {
        if (!map.has('status')) return;

        const status = map.get('status');
        if (!status) return;

        const overviewStatus = LeaseAgreementStatus[status[0].toUpperCase() + status.substring(1)];
        if (overviewStatus) this._overviewStatus = overviewStatus;
      })
    );
    this.AddSubscription(excelColumns.create(this.excelContext, this.excelColumnDefinition).subscribe(columns => {
      // Keep custom columns that may have arrived while the article lists were loading.
      const bindings = new Set(this.excelColumnDefinition.map(column => column.binding));
      this.excelColumnDefinition.push(...columns.filter(column => !bindings.has(column.binding)));
    }));

    this.title = 'LEASE_AGREEMENT.COOPERATIVE_HOUSING_LIST_HEADER';

    this.AddSubscription(
      _userAccount.current.subscribe({
        next: account => {
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.TRANSFER', leaseAgreementCommand.transferCooperativeHousing);
          } else {
            this.allowNew = false;
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.APPROVE_TRANSFER', leaseAgreementCommand.approve);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.WITHDRAW_APPROVAL', leaseAgreementCommand.withdrawApproval);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.CREATE_INVOICE', leaseAgreementCommand.createInvoice);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', leaseAgreementCommand.showInvoices);
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', leaseAgreementCommand.linkDocument);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_DOCUMENTS', leaseAgreementCommand.showDocuments);
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.BROKER_INFORMATION', rentalObjectCommand.printBrokerInformation);
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }

          if (account.hasAccess(ModuleNames.Messages, Features.ManageEmail)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_EMAIL', mailCommand.sendTenantEmail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageMail)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_LETTER', mailCommand.sendTenantMail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageSms)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_SMS', mailCommand.sendTenantSMS);
          }
        },
      })
    );
  }

  ngOnInit() {
    this.AddSubscription(
      this._leaseAgreementListService.getFilterDefinition().subscribe(definition => {
        this._customPropertyService.getList().subscribe(customProperties => {
          const effectiveDefinition = this.getEffectiveFilterDefinition(definition);
          this.addFilterDefinitions(effectiveDefinition);

          const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
            customProperties,
            effectiveDefinition,
            CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property | CustomPropertyVisibilityType.Building
          );
          this._ch.createCustomPropertyColumnDefinition(
            customProperties,
            this.columnDefinition,
            CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property | CustomPropertyVisibilityType.Building
          );
      this._ch.createCustomPropertyColumnDefinition(
            customProperties,
            this.excelColumnDefinition,
            CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property | CustomPropertyVisibilityType.Building
          );
          this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
        });
      })
    );

    this.AddSubscription(
      this._userAccount.current.subscribe({
        next: account => {
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
            this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          } else {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
          }
        },
      })
    );
  }

  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  private _overviewStatus: LeaseAgreementStatus;

  private getEffectiveFilterDefinition(definition: any): any {
    if (!this._overviewStatus) return definition;

    return {
      ...definition,
      status: this._overviewStatus,
      selectedFields: [...new Set([...(definition.selectedFields ?? []), 'status'])],
      company: [],
      property: [],
      tenantContactId: '',
      contactName: '',
      tenantContactIdentification: '',
      street: '',
      leaseAgreementNumber: '',
      rentalId: '',
      article: '',
      invoiceDeliveryMethod: '',
      subleaseStatus: SubleaseStatus.None,
      companyCustomPropertiesTextValues: [],
      companyCustomPropertiesDateValues: [],
      companyCustomPropertiesNumericValues: [],
      companyCustomPropertiesBooleanValues: [],
      rentalObjectCustomPropertiesTextValues: [],
      rentalObjectCustomPropertiesDateValues: [],
      rentalObjectCustomPropertiesNumericValues: [],
      rentalObjectCustomPropertiesBooleanValues: [],
      propertyCustomPropertiesTextValues: [],
      propertyCustomPropertiesDateValues: [],
      propertyCustomPropertiesNumericValues: [],
      propertyCustomPropertiesBooleanValues: [],
      buildingCustomPropertiesTextValues: [],
      buildingCustomPropertiesDateValues: [],
      buildingCustomPropertiesNumericValues: [],
      buildingCustomPropertiesBooleanValues: [],
    };
  }

  protected addFilterDefinitions(definition: any) {
    const ownerFilterField = definition.tenantContactId ? 'tenantContactId' : 'contactName';
    const ownerFilterValue = ownerFilterField === 'tenantContactId' ? definition.tenantContactId : definition.contactName;
    const selectedFields = definition.selectedFields.map(field =>
      field === 'tenantContactId' ? ownerFilterField : field
    );
    this.defaultFilters = definition.defaultFields.map(field => (field === 'tenantContactId' ? 'contactName' : field));
    const articles = definition.articles.map(article => {
      return { caption: article.toString(), value: article.id };
    });
    articles.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

    this.filterDefinition = [
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.STATUS',
        'status',
        definition.status,
        [
          { caption: 'ALL_TEXT', value: 0 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_ACTIVE', value: 9 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_DRAFT', value: LeaseAgreementStatus.Draft },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_COMING', value: LeaseAgreementStatus.Coming },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VALID', value: LeaseAgreementStatus.Valid },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_TERMINATED', value: LeaseAgreementStatus.Terminated },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_EXPIRED', value: LeaseAgreementStatus.Expired },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VOIDED', value: LeaseAgreementStatus.Voided },
        ],
        selectedFields.includes('status')
      ),
      new FilterDefinitionItem(
        'COMPANY.LIST_HEADER',
        'company',
        definition.company,
        this._companyService,
        selectedFields.includes('company'),
        true
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
        'property',
        definition.property,
        this._propertyService,
        selectedFields.includes('property'),
        true
      ),
      new FilterDefinitionItem(
        'COOPERATIVE_HOUSING.OWNER',
        ownerFilterField,
        ownerFilterValue,
        null,
        selectedFields.includes(ownerFilterField)
      ),
      new FilterDefinitionItem(
        'TENANT.CIVIC_ORG_NUMBER',
        'tenantContactIdentification',
        definition.tenantContactIdentification,
        null,
        selectedFields.includes('tenantContactIdentification')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_LIST.STREET_COLUMN_BRF',
        'street',
        definition.street,
        null,
        selectedFields.includes('street')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER',
        'leaseAgreementNumber',
        definition.leaseAgreementNumber,
        null,
        selectedFields.includes('leaseAgreementNumber')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
        'rentalId',
        definition.rentalId,
        null,
        selectedFields.includes('rentalId')
      ),
      new FilterDefinitionItem(
        'ARTICLE.EDIT_HEADER',
        'article',
        definition.article,
        articles,
        definition.selectedFields.includes('article')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_LIST.ROUND_RENTS',
        'roundRent',
        definition.roundRent,
        [
          { caption: 'YES_TEXT', value: true },
          { caption: 'NO_TEXT', value: false },
        ],
        definition.selectedFields.includes('roundRent')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD',
        'invoiceDeliveryMethod',
        definition.invoiceDeliveryMethod,
        [
          { caption: 'ALL_TEXT', value: '' },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Kivra), value: InvoiceDeliveryMethod.Kivra, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Kivra) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2C), value: InvoiceDeliveryMethod.EInvoiceB2C, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2C) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Sms), value: InvoiceDeliveryMethod.Sms, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Sms) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Avy), value: InvoiceDeliveryMethod.Avy, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Avy) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.NoDelivery), value: InvoiceDeliveryMethod.NoDelivery, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.NoDelivery) },
        ],
        definition.selectedFields.includes('invoiceDeliveryMethod')
      ),
      new FilterDefinitionItem(
        'SUBLEASE_REQUEST.SUBLEASE',
        'subleaseStatus',
        definition.subleaseStatus,
        [
          { caption: SubleaseStatusToString(SubleaseStatus.None), value: SubleaseStatus.None },
          { caption: SubleaseStatusToString(SubleaseStatus.Valid), value: SubleaseStatus.Valid },
          { caption: SubleaseStatusToString(SubleaseStatus.Coming), value: SubleaseStatus.Coming },
          { caption: SubleaseStatusToString(SubleaseStatus.Expired), value: SubleaseStatus.Expired },
        ],
        definition.selectedFields.includes('subleaseStatus')
      ),
    ];
  }
}
